import React, { useState, useCallback, useEffect } from 'react';
import { Box, useMediaQuery, useTheme, FormControl } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { Panel } from '../../../../../../../Components';
import { SelectField } from '../../../../../../../Components/Field';
import { TypedFormFieldValue } from '../../../../../../../Components/Form/Form.types';
import { useStyles } from '../../../../../../../Components/Field/Field.styles';
import { SelectOption } from '../../../../../../../Components/Form/Form.types';
import { StepButtonBar } from '../../../EditLegalListWizard/StepButtonBar';
import { useAptorApi } from '../../../../../../../Api';
import { IGridQueryRequest, OrderDirection } from '../../../../../../../Api/AptorApi';

interface Step1SelectCompanyUnitProps {
  onNext: (unit: SelectOption | SelectOption[]) => void;
  onBack: () => void;
  onCancel: () => void;
  isFirstStep: boolean;
  lawId: number;
  selectedCompanyUnit?: SelectOption;
  getCompanyUnitName: (formatMessage: any, isSingular: boolean) => string;
  updateChecklistInfo?: any;
  currentStepName?: string;
  setUpdateChecklistInfo?: () => void;
  companyUnitOptions?: SelectOption[];
  allowMultiple?: boolean;
}

const Step1SelectCompanyUnit: React.FC<Step1SelectCompanyUnitProps> = ({
  onNext,
  onBack,
  isFirstStep,
  lawId,
  selectedCompanyUnit,
  getCompanyUnitName,
  companyUnitOptions: providedCompanyUnitOptions,
  allowMultiple = false,
}) => {
  const [localSelected, setLocalSelected] = useState<SelectOption | SelectOption[] | undefined>(selectedCompanyUnit);
  const [companyUnitOptions, setCompanyUnitOptions] = useState<SelectOption[]>(providedCompanyUnitOptions || []);
  const [isLoading, setIsLoading] = useState(!providedCompanyUnitOptions);
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useStyles();
  const { api } = useAptorApi();

  useEffect(() => {
    if (providedCompanyUnitOptions) {
      setCompanyUnitOptions(providedCompanyUnitOptions);
      setIsLoading(false);
      return;
    }

    const loadCompanyUnits = async () => {
      setIsLoading(true);
      try {
        const request: IGridQueryRequest = {
          page: 0,
          pageSize: 100,
          orderBy: 'name',
          orderDirection: OrderDirection.Ascending,
        };
        const result = await api.searchLawCompanyUnits<any>(lawId, request);
        if (result && result.data) {
          const options = result.data.map((unit: any) => ({
            label: unit.name,
            value: unit.id,
          }));
          setCompanyUnitOptions(options);
        }
      } catch (error) {
        console.error('Failed to load company units:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCompanyUnits();
  }, [lawId, api, providedCompanyUnitOptions]);

  const handleChange = useCallback((val: TypedFormFieldValue<number> | TypedFormFieldValue<number>[] | undefined) => {
    if (val) {
      setLocalSelected(val as SelectOption | SelectOption[]);
    }
  }, []);

  const handleNext = () => {
    if (localSelected && (!Array.isArray(localSelected) || localSelected.length > 0)) {
      onNext(localSelected);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <Panel>
        <Box
          style={{
            padding: isMobile ? theme.spacing(1) : theme.spacing(2),
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'flex-start',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: theme.spacing(2),
          }}
        >
          <FormControl className={classes.formControl} style={{ width: isMobile ? '100%' : '45%' }}>
            <SelectField
              name="companyUnit"
              placeholder={formatMessage(
                { id: 'law-portal.manage.update-laws.dialog.companyUnit.placeholder' },
                { alias: getCompanyUnitName(formatMessage, true) },
              )}
              options={companyUnitOptions}
              isMulti={allowMultiple}
              errorTexts={[]}
              initialState={localSelected}
              onChange={handleChange}
            />
          </FormControl>
          <Box style={{ marginTop: isMobile ? 0 : -16 }}>
            <StepButtonBar
              onCancel={() => {}}
              onBack={isFirstStep ? undefined : onBack}
              onNext={handleNext}
              nextLabel="form.next"
              nextDisabled={!localSelected || (Array.isArray(localSelected) && localSelected.length === 0) || isLoading}
              formatMessage={formatMessage}
              showBack={false}
            />
          </Box>
        </Box>
      </Panel>
    </div>
  );
};

export default Step1SelectCompanyUnit;
